<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <reports-table
    module="pim/payroll"
    name="employee_pay_register"
    :prefetch="false"
    :filters="serializedFilters"
    :column-count="2"
  >
    <template #default="{generateReport}">
      <oxd-table-filter filter-title="Employee Pay Register">
        <oxd-form @submit-valid="generateReport">
          <oxd-form-row>
            <oxd-grid :cols="3" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <employee-autocomplete
                  v-model="filters.employee"
                  :rules="rules.employee"
                  :params="{
                    includeEmployees: 'currentAndPast',
                  }"
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <employment-status-dropdown v-model="filters.empStatus" />
              </oxd-grid-item>
              <oxd-grid-item>
                <date-input
                  v-model="filters.fromDate"
                  :placeholder="$t('general.from')"
                  :rules="rules.fromDate"
                  label="Select month with any date"
                  required
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>

          <oxd-divider />

          <oxd-form-actions>
            <oxd-button
              ref="btnRef"
              type="submit"
              display-type="secondary"
              :label="$t('general.view')"
            />
            <oxd-button
              class="orangehrm-left-space"
              type="button"
              display-type="secondary"
              label="Release Payslips"
              @click="onConfirm"
            />
            <oxd-button
              class="orangehrm-left-space"
              type="button"
              display-type="secondary"
              label="Bulk Update"
              @click.prevent="onClickImport"
            />
          </oxd-form-actions>
        </oxd-form>
      </oxd-table-filter>
      <br />
    </template>
  </reports-table>
  <confirmation-dialog
    ref="confirmDialog"
    :title="$t('general.confirmation_required')"
    subtitle="Are you sure?"
    :cancel-label="$t('general.no_cancel')"
    :confirm-label="$t('leave.yes_confirm')"
    confirm-button-type="secondary"
  ></confirmation-dialog>
</template>

<script>
import {computed, ref} from 'vue';
import {
  required,
  validSelection,
  validDateFormat,
  startDateShouldBeBeforeEndDate,
} from '@/core/util/validation/rules';
import ReportsTable from '@/core/components/table/ReportsTable';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import EmploymentStatusDropdown from '@/orangehrmPimPlugin/components/EmploymentStatusDropdown';
import usei18n from '@/core/util/composable/usei18n';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {APIService} from '@ohrm/core/util/services/api.service';
import ConfirmationDialog from '@/core/components/dialogs/ConfirmationDialog';

const defaultFilters = {
  employee: null,
  fromDate: null,
};

export default {
  components: {
    'reports-table': ReportsTable,
    'employee-autocomplete': EmployeeAutocomplete,
    'employment-status-dropdown': EmploymentStatusDropdown,
    'confirmation-dialog': ConfirmationDialog,
  },

  props: {
    subunits: {
      type: Array,
      default: () => [],
    },
  },

  setup() {
    const filters = ref({
      ...defaultFilters,
    });
    const {$t} = usei18n();
    const {userDateFormat} = useDateFormat();

    const rules = {
      fromDate: [
        required,
        validDateFormat(userDateFormat),
        startDateShouldBeBeforeEndDate(
          () => filters.value.toDate,
          $t('general.from_date_should_be_before_to_date'),
          {allowSameDate: true},
        ),
      ],
      employee: [validSelection],
    };

    const serializedFilters = computed(() => {
      return {
        empNumber: filters.value.employee?.id,
        fromDate: filters.value.fromDate,
        toDate: filters.value.toDate,
        jobTitleId: filters.value.jobTitle?.id,
        subunitId: filters.value.subunit?.id,
        employmentStatusId: filters.value.empStatus?.id,
      };
    });
    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/pim/release-employee-payslip`,
    );

    return {
      rules,
      filters,
      serializedFilters,
      http,
    };
  },
  methods: {
    onClickImport() {
      const importUrl = `${window.appGlobal.baseUrl}/pim/pimPaymentRegisterCsvImport`;
      window.location.href = importUrl;
    },
    onConfirm() {
      this.$refs.confirmDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') this.releasePaySlip();
      });
    },
    releasePaySlip() {
      this.isLoading = true;
      this.http
        .request({
          method: 'PUT',
          data: {
            ...this.serializedFilters,
          },
        })
        .then(() => {
          return this.$toast.updateSuccess();
        })
        .then(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
